
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Gift, Clock, ArrowRight, ArrowLeft, Loader2, Calendar, FileText, AlertCircle, MapPin, Locate, UploadCloud, Trash2, Check, CheckCircle2, LogIn, ArrowUp, ArrowDown, Box, RefreshCw, ShieldCheck, Fingerprint, Lock } from 'lucide-react';
import { submitSurveyResponse, uploadSurveyFile } from '../services/surveyService';
import { Survey, SurveyQuestionDraft, LocalizedText, LootItem } from '../types';
import { supabase } from '../services/supabaseClient';
import { validateSurveyResponse } from '../services/geminiService';
import { useZkVault } from '../services/zkVault/hooks';
import { AttributeValue } from '../services/zkVault/types';
import { shareAttributes } from '../services/disclosureService';
import { summarizeAttributeValue } from '../services/attributesService';

// Helpers for Localization Priority
// Goal: If a non-English language exists (e.g. Khmer), show it first. English becomes secondary.
const getPrimaryText = (obj: LocalizedText): string => {
    const keys = Object.keys(obj);
    if (keys.length === 0) return 'Untitled';
    // Find the first key that IS NOT 'en'
    const nonEnKey = keys.find(k => k !== 'en');
    // If found, return it. Otherwise return 'en' (or the first available).
    const val = nonEnKey ? obj[nonEnKey] : (obj.en || Object.values(obj)[0]);
    return val || 'Untitled';
};

const getSecondaryText = (obj: LocalizedText) => {
    const keys = Object.keys(obj);
    const nonEnKey = keys.find(k => k !== 'en');
    // If we are showing a foreign language as primary, and we have English, show English as secondary.
    if (nonEnKey && obj.en) return obj.en;
    return null;
};

// Best-effort mapping between a vault attribute value and a linked question's answer.
const vaultToAnswer = (qType: string, options: any[] | undefined, value: AttributeValue): any => {
  if (value === undefined || value === null || value === '') return undefined;
  if (qType === 'TEXT' || qType === 'DATE') return String(value);
  if (qType === 'MULTIPLE_CHOICE' && Array.isArray(options)) {
    const target = String(value).toLowerCase();
    const labelMatches = (o: any, re: RegExp) =>
      Object.values(o.label || {}).some((l: any) => re.test(String(l).toLowerCase()));
    const match = options.find((o: any) =>
      o.id === value ||
      String(o.id).toLowerCase() === target ||
      Object.values(o.label || {}).some((l: any) => String(l).toLowerCase() === target) ||
      (value === true && labelMatches(o, /^(yes|own|true)/)) ||
      (value === false && labelMatches(o, /^(no|don|false)/)));
    return match ? match.id : undefined;
  }
  return undefined;
};

const answerToVault = (qType: string, options: any[] | undefined, dataType: string | undefined, answer: any): AttributeValue | undefined => {
  if (answer === undefined || answer === null || answer === '') return undefined;
  let raw: any;
  if (qType === 'TEXT' || qType === 'DATE') raw = String(answer);
  else if (qType === 'MULTIPLE_CHOICE' && Array.isArray(options)) {
    const opt = options.find((o: any) => o.id === answer);
    if (!opt) return undefined;
    raw = (opt.label && (opt.label.en || Object.values(opt.label)[0])) || String(answer);
  } else return undefined;
  if (dataType === 'NUMBER') { const n = Number(raw); return Number.isFinite(n) ? n : undefined; }
  if (dataType === 'BOOLEAN') return /^(yes|own|true)/i.test(String(raw));
  return raw;
};

// A question counts as answered only with real content — empty array (CHECKBOX/RANKING)
// and empty object (MATRIX) are NOT answers, so a required question can't be skipped.
// A required MATRIX needs EVERY row answered, not just one — otherwise "required"
// silently accepts a 1-of-N partial grid.
const isAnswered = (q: SurveyQuestionDraft, val: any): boolean => {
  if (q.question_type === 'MATRIX') {
    if (!val || typeof val !== 'object') return false;
    const rows = q.config.rows || [];
    if (rows.length === 0) return Object.keys(val).length > 0;
    return rows.every(r => !!val[r.id]);
  }
  if (Array.isArray(val)) return val.length > 0;
  return val !== undefined && val !== null && val !== '';
};

interface NormalViewProps {
    survey: Survey;
    questions: SurveyQuestionDraft[];
}

const SurveyResponseView: React.FC<NormalViewProps> = ({ survey, questions }) => {
  const navigate = useNavigate();
  const vault = useZkVault();
  const [viewState, setViewState] = useState<'INTRO' | 'RUNNER' | 'CONSENT' | 'SUCCESS'>('INTRO');

  // User State
  const [isGuest, setIsGuest] = useState(true);

  // DataFi consent: which vault fields this survey requests (logged-in users only)
  const requestedFields = survey.requested_attribute_ids || [];
  // Questions linked to a vault attribute (age, gender, …) — pre-fill + save-back.
  const linkedQuestions = useMemo(() => questions.filter(q => q.attribute_id), [questions]);
  const [savedToVaultCount, setSavedToVaultCount] = useState(0);
  const prefilledRef = useRef(false);
  
  // Runner State
  const [currentQIndex, setCurrentQIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [historyStack, setHistoryStack] = useState<number[]>([]); 
  
  // Validation
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Reward / Mystery Box State
  const [wonItem, setWonItem] = useState<LootItem | null>(null);
  const [isUnboxing, setIsUnboxing] = useState(false); 
  const [boxOpen, setBoxOpen] = useState(false);

  useEffect(() => {
    checkUser();
  }, []);

  // Pre-fill linked questions from the respondent's unlocked vault (best-effort; the
  // value is editable). No-op if the vault is locked or the question is already answered.
  useEffect(() => {
    if (prefilledRef.current || vault.status !== 'unlocked' || linkedQuestions.length === 0) return;
    const seed: Record<string, any> = {};
    for (const q of linkedQuestions) {
      if (answers[q.id] !== undefined) continue;
      const mapped = vaultToAnswer(q.question_type, q.config?.options, vault.attributes[q.attribute_id!]);
      if (mapped !== undefined) seed[q.id] = mapped;
    }
    if (Object.keys(seed).length) {
      setAnswers(prev => ({ ...seed, ...prev }));
      prefilledRef.current = true;
    }
  }, [vault.status, vault.attributes, linkedQuestions]);

  // Auto-Open Box Logic for Normal Mode
  useEffect(() => {
      if (viewState === 'SUCCESS' && wonItem && !boxOpen && !isUnboxing) {
          const timer = setTimeout(() => {
              handleOpenBox();
          }, 500); 
          return () => clearTimeout(timer);
      }
  }, [viewState, wonItem]);

  const checkUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setIsGuest(!session);
  };

  const startSurvey = () => {
    setAnswers({});
    setCurrentQIndex(0);
    setHistoryStack([]);
    setWonItem(null);
    setBoxOpen(false);
    setIsUnboxing(false);
    setViewState('RUNNER');
  };

  const handleLoginRedirect = () => {
      navigate('/login'); 
  };

  const handleAnswerChange = (value: any) => {
    const currentQ = questions[currentQIndex];
    setAnswers(prev => ({ ...prev, [currentQ.id]: value }));
    setValidationError(null);
  };

  const nextQuestion = async () => {
    const currentQ = questions[currentQIndex];
    const answer = answers[currentQ.id];

    // Enforce required in logic, not just via the disabled button (defence in depth).
    if (currentQ.config.required !== false && !isAnswered(currentQ, answer)) {
      setValidationError('Please answer this question before continuing.');
      return;
    }

    // AI Validation
    if (currentQ.gemini_validation_prompt && answer && typeof answer === 'string' && answer.length > 5) {
        setIsValidating(true);
        try {
            const result = await validateSurveyResponse(
                getPrimaryText(currentQ.question_text), 
                answer, 
                currentQ.gemini_validation_prompt
            );
            if (!result.isValid) {
                setValidationError(result.feedback || "Please provide a more detailed answer.");
                setIsValidating(false);
                return;
            }
        } catch (e) {
            console.warn("Validation check failed, allowing progress.");
        } finally {
            setIsValidating(false);
        }
    }

    setTimeout(() => {
        setHistoryStack(prev => [...prev, currentQIndex]);
        let nextIndex = currentQIndex + 1;

        // Skip Logic
        if (currentQ.question_type === 'MULTIPLE_CHOICE') {
            const selectedOptionId = answers[currentQ.id];
            const selectedOption = currentQ.config.options?.find(o => o.id === selectedOptionId);
            if (selectedOption?.action === 'SUBMIT') {
                proceedToSubmit();
                return;
            }
            if (selectedOption?.next_question_index) {
                const targetIndex = questions.findIndex(q => q.order_index === selectedOption.next_question_index);
                if (targetIndex !== -1) nextIndex = targetIndex;
            }
        } else if (currentQ.config.next_question_index) {
             const targetIndex = questions.findIndex(q => q.order_index === currentQ.config.next_question_index);
             if (targetIndex !== -1) nextIndex = targetIndex;
        }

        // Guard the lower bound too: a skip rule pointing to a non-existent order_index
        // would otherwise leave nextIndex from a bad findIndex and render questions[-1].
        if (nextIndex >= 0 && nextIndex < questions.length) {
            setCurrentQIndex(nextIndex);
        } else {
            proceedToSubmit();
        }
    }, 150);
  };

  const prevQuestion = () => {
    setValidationError(null);
    if (historyStack.length > 0) {
      const prevIndex = historyStack[historyStack.length - 1];
      setHistoryStack(prev => prev.slice(0, -1));
      setCurrentQIndex(prevIndex);
    } else {
      setViewState('INTRO');
    }
  };

  // When the runner finishes, route through the consent step if this survey
  // requests private data AND the respondent is signed in (guests have no vault).
  const proceedToSubmit = () => {
    if (requestedFields.length > 0 && !isGuest) {
      setViewState('CONSENT');
    } else {
      finalizeSubmit(null);
    }
  };

  const finalizeSubmit = async (sharedFields: Record<string, AttributeValue> | null) => {
    setIsSubmitting(true);
    try {
      const answerArray = Object.entries(answers).map(([qId, val]) => ({ questionId: qId, value: val }));
      const result = await submitSurveyResponse(survey.id, answerArray);

      // If the user consented, store the disclosure and award the data reward
      // (reputation + inventory) via the RPC.
      if (sharedFields && Object.keys(sharedFields).length > 0 && result?.id) {
        try {
          await shareAttributes(survey.id, result.id, sharedFields);
        } catch (shareErr) {
          // The response is already saved; don't fail the whole submission on a
          // sharing hiccup — just log it.
          console.error('Data sharing failed:', shareErr);
        }
      }

      // Save-back: enrich the vault with linked answers it doesn't have yet — only when
      // the vault is already unlocked (consent implied). Never blocks the submission.
      if (vault.status === 'unlocked' && linkedQuestions.length > 0) {
        let saved = 0;
        for (const q of linkedQuestions) {
          const attrId = q.attribute_id!;
          const existing = vault.attributes[attrId];
          if (existing !== undefined && existing !== null && existing !== '') continue;
          const def = vault.definitions.find(d => d.id === attrId);
          const val = answerToVault(q.question_type, q.config?.options, def?.data_type, answers[q.id]);
          if (val === undefined) continue;
          try { await vault.saveAttribute(attrId, val); saved++; } catch (err) { console.error('Vault save-back failed', err); }
        }
        if (saved > 0) setSavedToVaultCount(saved);
      }

      // Check for won item (Physical Reward / Kiosk Logic applied to Normal too if applicable)
      if (result && result.wonItem) {
          setWonItem(result.wonItem);
      }

      setViewState('SUCCESS');
    } catch (e: any) {
      console.error(e);
      // Surface the real reason (survey closed / full) instead of a generic error.
      alert(e?.message || "Submission failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenBox = () => {
      setIsUnboxing(true);
      setTimeout(() => { setBoxOpen(true); setIsUnboxing(false); }, 3000); // 3s Animation
  };

  // --- RENDERERS ---

  if (viewState === 'SUCCESS') {
      return (
        <div className="w-full min-h-screen flex flex-col items-center justify-center bg-slate-900 p-6 text-center animate-in fade-in zoom-in duration-500 overflow-hidden relative">
            <style dangerouslySetInnerHTML={{__html: `
                @keyframes shake {
                  0% { transform: translate(1px, 1px) rotate(0deg); }
                  10% { transform: translate(-1px, -2px) rotate(-1deg); }
                  20% { transform: translate(-3px, 0px) rotate(1deg); }
                  30% { transform: translate(3px, 2px) rotate(0deg); }
                  40% { transform: translate(1px, -1px) rotate(1deg); }
                  50% { transform: translate(-1px, 2px) rotate(-1deg); }
                  60% { transform: translate(-3px, 1px) rotate(0deg); }
                  70% { transform: translate(3px, 1px) rotate(-1deg); }
                  80% { transform: translate(-1px, -1px) rotate(1deg); }
                  90% { transform: translate(1px, 2px) rotate(0deg); }
                  100% { transform: translate(1px, -2px) rotate(-1deg); }
                }
                .animate-box-shake {
                  animation: shake 0.5s;
                  animation-iteration-count: infinite;
                }
            `}} />

            <div className="max-w-xl w-full relative z-10">
                {wonItem ? (
                    <div className="relative">
                        {!boxOpen ? (
                             <div className="cursor-pointer" onClick={handleOpenBox}>
                                 <div className={`transition-transform duration-300 ${isUnboxing ? 'animate-box-shake' : 'hover:scale-105'}`}>
                                     <Box size={160} className="text-yellow-400 mx-auto drop-shadow-[0_0_40px_rgba(250,204,21,0.6)]" strokeWidth={1} />
                                     <p className="mt-6 text-2xl font-bold text-white animate-pulse">
                                         {isUnboxing ? 'Unlocking Reward...' : 'Tap to Open Box!'}
                                     </p>
                                 </div>
                             </div>
                        ) : (
                             <div className="bg-slate-800 rounded-2xl p-8 shadow-2xl border-2 border-indigo-500 animate-in zoom-in duration-500">
                                 <div className="text-center mb-6">
                                     <span className="bg-indigo-600 text-white px-4 py-1 rounded-full text-xs font-bold uppercase tracking-widest shadow">You Won</span>
                                     <h2 className="text-2xl font-bold text-white mt-4">{wonItem.name}</h2>
                                 </div>
                                 <div className="bg-slate-900/50 rounded-xl p-6 mb-6 flex items-center justify-center h-48 border border-slate-700">
                                     {wonItem.image_url ? (
                                         <img src={wonItem.image_url} alt={wonItem.name} className="h-full object-contain" />
                                     ) : (
                                         <Gift size={64} className="text-indigo-400" />
                                     )}
                                 </div>
                                 <p className="text-slate-400 text-sm mb-6">
                                     This item has been added to your inventory.
                                 </p>
                                 <button onClick={() => navigate('/earn/gifts')} className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-bold py-3 rounded-xl shadow-lg transition-all flex items-center justify-center gap-2">
                                     <RefreshCw size={18} /> View Inventory
                                 </button>
                             </div>
                        )}
                    </div>
                ) : (
                    <>
                        <div className="w-24 h-24 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6 text-emerald-400 border border-emerald-500/30 shadow-[0_0_30px_rgba(16,185,129,0.3)]">
                            <CheckCircle2 size={48} />
                        </div>
                        <h1 className="text-4xl font-bold text-white mb-4">Thank You!</h1>
                        <p className="text-slate-400 text-lg mb-8">Your response has been recorded successfully.</p>
                        {savedToVaultCount > 0 && (
                            <p className="text-emerald-300 text-sm mb-8 inline-flex items-center gap-2 bg-emerald-900/20 border border-emerald-500/30 rounded-lg px-3 py-2">
                                <ShieldCheck size={16} /> Saved {savedToVaultCount} answer{savedToVaultCount > 1 ? 's' : ''} to your encrypted vault for next time.
                            </p>
                        )}
                        <button onClick={() => navigate(isGuest ? '/' : '/earn')} className="bg-indigo-600 hover:bg-indigo-500 text-white px-8 py-3 rounded-lg font-bold shadow-lg transition-all">
                            {isGuest ? 'Back to Home' : 'Back to Dashboard'}
                        </button>
                    </>
                )}
            </div>
        </div>
      );
  }

  if (viewState === 'RUNNER') {
      const currentQ = questions[currentQIndex];
      const answerVal = answers[currentQ.id];
      const hasAnswer = isAnswered(currentQ, answerVal);

      const progress = ((currentQIndex + 1) / questions.length) * 100;

      return (
          <div className="min-h-screen flex flex-col bg-slate-900">
              {/* Header */}
              <div className="w-full bg-slate-800/50 backdrop-blur border-b border-slate-700 px-4 md:px-8 py-4 flex items-center justify-between sticky top-0 z-20">
                  <div className="flex items-center gap-4 flex-1">
                      <button onClick={prevQuestion} className="text-slate-400 hover:text-white p-2 -ml-2 rounded-full hover:bg-slate-700 transition-colors">
                          <ArrowLeft size={24} />
                      </button>
                      <div className="flex-1 max-w-md h-2 bg-slate-700 rounded-full overflow-hidden">
                          <div className="h-full bg-indigo-500 transition-all duration-300" style={{ width: `${progress}%` }} />
                      </div>
                      <span className="text-xs font-mono text-slate-400 hidden sm:block">{currentQIndex + 1} / {questions.length}</span>
                  </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto">
                  <div className="max-w-4xl mx-auto px-4 py-8 md:py-12">
                      <div className="mb-8 md:mb-12 text-center">
                          <span className="inline-block text-indigo-400 text-xs font-bold uppercase tracking-wider mb-3 bg-indigo-900/20 px-3 py-1 rounded-full">
                              Question {currentQIndex + 1}
                          </span>
                          
                          {/* Priority: Non-English First */}
                          <h2 className="text-2xl md:text-4xl font-bold text-white leading-tight mb-3">
                              {getPrimaryText(currentQ.question_text)}
                          </h2>
                          
                          {getSecondaryText(currentQ.question_text) && (
                              <p className="text-lg md:text-2xl text-slate-400 font-khmer">
                                  {getSecondaryText(currentQ.question_text)}
                              </p>
                          )}
                      </div>

                      <div className="animate-in slide-in-from-bottom-4 duration-300 fade-in">
                          <NormalInput q={currentQ} value={answers[currentQ.id]} onChange={handleAnswerChange} />
                      </div>
                      
                      {validationError && (
                          <div className="max-w-lg mx-auto mt-6 bg-red-900/20 border border-red-500/50 text-red-200 p-4 rounded-xl flex items-start gap-3 animate-in shake">
                              <AlertCircle className="shrink-0 mt-0.5 text-red-400" />
                              <div>
                                  <p className="font-bold text-sm text-red-400 mb-1">Quality Check Failed</p>
                                  <p className="text-sm">{validationError}</p>
                              </div>
                          </div>
                      )}
                  </div>
              </div>

              {/* Footer */}
              <div className="bg-slate-800/90 backdrop-blur border-t border-slate-700 p-4 md:p-6 sticky bottom-0 z-20">
                  <div className="max-w-4xl mx-auto flex justify-between items-center gap-4">
                      <button onClick={() => setViewState('INTRO')} className="text-slate-500 hover:text-white text-sm">
                          Cancel
                      </button>
                      <button 
                          onClick={() => nextQuestion()}
                          disabled={(currentQ.config.required !== false && !hasAnswer) || isValidating}
                          className={`ml-auto px-8 py-3.5 rounded-xl font-bold text-lg flex items-center gap-2 transition-all ${
                              (currentQ.config.required !== false && !hasAnswer) || isValidating 
                              ? 'bg-slate-700 text-slate-500 cursor-not-allowed' 
                              : 'bg-indigo-600 hover:bg-indigo-500 text-white shadow-lg active:scale-95'
                          }`}
                      >
                          {isValidating ? (
                              <>Checking <Loader2 className="animate-spin" size={20} /></>
                          ) : (
                              <>
                                  {currentQIndex === questions.length - 1 ? 'Finish' : 'Next'} 
                                  {isSubmitting ? <Loader2 className="animate-spin" size={20} /> : <ArrowRight size={20} />}
                              </>
                          )}
                      </button>
                  </div>
              </div>
          </div>
      );
  }

  if (viewState === 'CONSENT') {
    return (
      <ConsentStep
        survey={survey}
        requestedFields={requestedFields}
        isSubmitting={isSubmitting}
        onShare={(fields) => finalizeSubmit(fields)}
        onSkip={() => finalizeSubmit(null)}
      />
    );
  }

  // INTRO VIEW
  const isActive = survey.status === 'ACTIVE';

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 relative overflow-hidden">
        <div className="relative z-10 max-w-3xl w-full mx-auto flex flex-col items-center text-center p-6">
            <div className="mb-8">
                <div className="w-20 h-20 bg-indigo-500/20 rounded-2xl flex items-center justify-center text-indigo-400 mx-auto backdrop-blur-xl border border-indigo-500/30 shadow-lg shadow-indigo-500/10">
                    <FileText size={40} />
                </div>
            </div>

            {/* Priority: Non-English First */}
            <h1 className="text-4xl md:text-6xl font-black text-white mb-4 tracking-tight leading-tight">
                {getPrimaryText(survey.title)}
            </h1>
            
            {getSecondaryText(survey.title) && (
                 <h2 className="text-xl md:text-2xl text-slate-400 font-khmer mb-6">{getSecondaryText(survey.title)}</h2>
            )}

            <div className="flex flex-wrap justify-center gap-3 mb-8">
                <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-slate-800/50 border border-slate-700 text-sm font-medium text-slate-300">
                    <Clock size={16} className="text-indigo-400" />
                    <span>~{Math.max(1, Math.round(questions.length * 0.5))} min</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-slate-800/50 border border-slate-700 text-sm font-medium text-slate-300">
                    <Gift size={16} className="text-emerald-400" />
                    <span>{survey.reward_type === 'NONE' ? 'Volunteer' : (survey.reward_type === 'DIGITAL' ? 'Crypto Reward' : 'Loot Box')}</span>
                </div>
            </div>

            <p className="text-lg text-slate-300 leading-relaxed max-w-2xl mb-12">
                {getPrimaryText(survey.description)}
            </p>

            {isActive ? (
                survey.require_login && isGuest ? (
                    <button 
                        onClick={handleLoginRedirect}
                        className="bg-indigo-600 hover:bg-indigo-500 text-white text-lg md:text-xl font-bold py-4 px-10 rounded-full shadow-xl hover:shadow-indigo-500/20 active:scale-95 transition-all flex items-center justify-center gap-3"
                    >
                        <LogIn size={24} /> Login to Participate
                    </button>
                ) : (
                    <button 
                        onClick={startSurvey}
                        className="bg-indigo-600 hover:bg-indigo-500 text-white text-lg md:text-xl font-bold py-4 px-12 rounded-full shadow-xl hover:shadow-indigo-500/20 active:scale-95 transition-all flex items-center justify-center gap-3"
                    >
                        Start Survey <ArrowRight size={24} />
                    </button>
                )
            ) : (
                <div className="p-4 rounded-xl border border-slate-700 bg-slate-800 text-slate-400">
                    Survey is currently {survey.status.toLowerCase()}.
                </div>
            )}
            
            {isGuest && isActive && !survey.require_login && (
                <div className="mt-8">
                    <p className="text-xs text-slate-500 flex items-center gap-1.5 justify-center">
                        <AlertCircle size={12} className="text-orange-400" />
                        Guest Mode: Contributing anonymously. 
                        <button onClick={handleLoginRedirect} className="text-indigo-400 hover:text-indigo-300 underline ml-1">
                            Login for rewards
                        </button>
                    </p>
                </div>
            )}
        </div>
    </div>
  );
};

// --- SUB-COMPONENT: DATA-SHARING CONSENT ---
function ConsentStep({
  survey,
  requestedFields,
  isSubmitting,
  onShare,
  onSkip,
}: {
  survey: Survey;
  requestedFields: string[];
  isSubmitting: boolean;
  onShare: (fields: Record<string, AttributeValue>) => void;
  onSkip: () => void;
}) {
  const vault = useZkVault();
  const navigate = useNavigate();
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState<'pin' | 'passkey' | null>(null);

  const labelFor = (id: string) =>
    vault.definitions.find((d) => d.id === id)?.label_en || id;

  const rewardPoints = survey.data_reward_points || 0;
  const hasReward = rewardPoints > 0;
  const targets = survey.target_attributes || [];

  // Eligibility is checked privately on-device once unlocked (the server re-checks
  // authoritatively before awarding). Null = unknown (still locked / no targeting).
  const eligible = useMemo<boolean | null>(() => {
    if (targets.length === 0) return null;
    if (vault.status !== 'unlocked') return null;
    return targets.every((t) => {
      const def = vault.definitions.find((d) => d.id === t.attribute_id);
      if (!def) return false;
      return summarizeAttributeValue(def.data_type, vault.attributes[t.attribute_id]) === t.value;
    });
  }, [targets, vault.status, vault.attributes, vault.definitions]);

  const collectAndShare = () => {
    onShare(vault.pickAttributes(requestedFields));
  };

  const unlockPin = async () => {
    setBusy('pin');
    try {
      await vault.unlockWithPin(pin);
    } catch {
      /* surfaced via vault.error */
    } finally {
      setBusy(null);
      setPin('');
    }
  };

  const unlockPasskey = async () => {
    setBusy('passkey');
    try {
      await vault.unlockWithPasskey();
    } catch {
      /* surfaced */
    } finally {
      setBusy(null);
    }
  };

  const canPasskey = vault.hasPasskey && vault.capability.webauthnSupported;

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 p-4">
      <div className="max-w-lg w-full bg-slate-800 rounded-2xl border border-slate-700 shadow-2xl p-6 space-y-5">
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 rounded-xl bg-emerald-900/20 border border-emerald-500/30 flex items-center justify-center shrink-0">
            <ShieldCheck className="text-emerald-400" size={22} />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">Share private data &amp; earn</h2>
            <p className="text-slate-400 text-sm mt-0.5">
              This researcher would like to use some data from your encrypted vault. Sharing is
              optional — you choose.
            </p>
          </div>
        </div>

        <div className="bg-slate-900 rounded-xl border border-slate-700 p-4">
          <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
            Requested fields
          </p>
          <div className="flex flex-wrap gap-2">
            {requestedFields.map((k) => (
              <span
                key={k}
                className="text-xs bg-slate-800 border border-slate-600 text-slate-200 px-2.5 py-1 rounded-lg"
              >
                {labelFor(k)}
              </span>
            ))}
          </div>
          {hasReward && (
            <div className="mt-3 flex items-center gap-2 text-emerald-300 text-sm">
              <Gift size={15} />
              Reward for sharing: <strong>{rewardPoints} reputation points</strong>
            </div>
          )}
          {targets.length > 0 && (
            <div className={`mt-2 text-xs flex items-start gap-1.5 ${eligible === false ? 'text-amber-300' : 'text-slate-400'}`}>
              {eligible === true && <><Check size={13} className="mt-0.5 text-emerald-400" /> You match this survey's audience — you'll earn the reward.</>}
              {eligible === false && <><AlertCircle size={13} className="mt-0.5 shrink-0" /> You don't match this survey's targeted audience, so sharing won't earn the reward. You can still share or skip.</>}
              {eligible === null && <span className="text-slate-500">This survey targets a specific audience; unlock to see if you qualify for the reward.</span>}
            </div>
          )}
        </div>

        {vault.error && (
          <div className="flex items-start gap-2 bg-red-900/20 border border-red-500/40 text-red-200 px-3 py-2.5 rounded-xl text-sm">
            <AlertCircle size={15} className="shrink-0 mt-0.5 text-red-400" />
            {vault.error}
          </div>
        )}

        {/* State-dependent body */}
        {vault.status === 'loading' && (
          <div className="flex justify-center py-4">
            <Loader2 className="animate-spin text-indigo-500" size={28} />
          </div>
        )}

        {vault.status === 'uninitialized' && (
          <div className="text-sm text-slate-300 bg-slate-900 border border-slate-700 rounded-xl p-4">
            You don't have a vault yet. Create one to store private data and earn when you share it.
            <button
              onClick={() => navigate('/privacy-vault')}
              className="mt-3 w-full bg-indigo-600 hover:bg-indigo-500 text-white font-semibold py-2.5 rounded-lg"
            >
              Set up my vault
            </button>
            <p className="text-[11px] text-slate-500 mt-2">You can come back and take this survey afterwards.</p>
          </div>
        )}

        {vault.status === 'locked' && (
          <div className="space-y-3">
            {vault.hasPin && (
              <input
                type="password"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && pin && unlockPin()}
                placeholder="Enter your vault PIN to unlock"
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2.5 text-slate-200 focus:outline-none focus:border-indigo-500 text-sm"
                autoComplete="current-password"
              />
            )}
            {vault.hasPin && (
              <button
                onClick={unlockPin}
                disabled={!pin || busy !== null}
                className="w-full flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-700 text-white font-semibold py-2.5 rounded-lg"
              >
                {busy === 'pin' ? <Loader2 className="animate-spin" size={16} /> : <Lock size={16} />} Unlock with PIN
              </button>
            )}
            {canPasskey && (
              <button
                onClick={unlockPasskey}
                disabled={busy !== null}
                className="w-full flex items-center justify-center gap-2 bg-slate-700 hover:bg-slate-600 text-white font-semibold py-2.5 rounded-lg"
              >
                {busy === 'passkey' ? <Loader2 className="animate-spin" size={16} /> : <Fingerprint size={16} />} Unlock with passkey
              </button>
            )}
          </div>
        )}

        {vault.status === 'unlocked' && (
          <button
            onClick={collectAndShare}
            disabled={isSubmitting}
            className="w-full flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-700 text-white font-bold py-3 rounded-lg"
          >
            {isSubmitting ? <Loader2 className="animate-spin" size={18} /> : <ShieldCheck size={18} />}
            Share &amp; submit{hasReward ? ` — earn ${rewardPoints} points` : ''}
          </button>
        )}

        <button
          onClick={onSkip}
          disabled={isSubmitting}
          className="w-full text-slate-400 hover:text-white text-sm py-2 disabled:opacity-50"
        >
          {isSubmitting ? 'Submitting…' : 'Skip & submit without sharing'}
        </button>
      </div>
    </div>
  );
}

// --- SUB-COMPONENT: NORMAL INPUTS ---
const NormalInput = ({ q, value, onChange }: { q: SurveyQuestionDraft, value: any, onChange: (v: any) => void }) => {
    // Image-upload in-flight state (declared unconditionally to satisfy hook rules).
    const [uploading, setUploading] = useState(false);
    switch (q.question_type) {
        case 'TEXT':
            return <textarea value={value || ''} onChange={e => onChange(e.target.value)} placeholder="Type answer..." className="w-full h-40 bg-slate-800 border border-slate-700 rounded-xl p-4 text-white text-lg focus:ring-2 focus:ring-indigo-500 outline-none resize-none" autoFocus />;
        case 'RATING':
            // Harden against a broken config (min >= max, or NaN from the builder): a raw
            // range computation would render ZERO buttons and trap a required question.
            let rMin = Number.isFinite(q.config.min as number) ? Number(q.config.min) : 1;
            let rMax = Number.isFinite(q.config.max as number) ? Number(q.config.max) : 5;
            if (rMax < rMin) [rMin, rMax] = [rMax, rMin];
            if (rMax === rMin) rMax = rMin + 4;
            const range = Array.from({length: Math.min(rMax - rMin + 1, 21)}, (_, i) => rMin + i);
            return (
                <div className="flex flex-wrap justify-center gap-4">
                    {range.map(v => (
                        <button key={v} onClick={() => onChange(v)} className={`w-14 h-14 rounded-full font-bold text-xl border-2 transition-all ${value === v ? 'bg-indigo-600 border-indigo-500 text-white shadow-lg' : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-500'}`}>{v}</button>
                    ))}
                </div>
            );
        case 'MULTIPLE_CHOICE':
            return (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto">
                    {q.config.options?.map(opt => (
                        <button key={opt.id} onClick={() => onChange(opt.id)} className={`p-4 rounded-xl border-2 text-left transition-all ${value === opt.id ? 'bg-indigo-600 border-indigo-500 shadow-lg' : 'bg-slate-800 border-slate-700 hover:border-slate-500'}`}>
                            <span className="font-bold text-white">{getPrimaryText(opt.label)}</span>
                        </button>
                    ))}
                </div>
            );
        case 'CHECKBOX':
             const selected = (value as string[]) || [];
             const toggle = (id: string) => onChange(selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id]);
             return (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto">
                    {q.config.options?.map(opt => (
                        <button key={opt.id} onClick={() => toggle(opt.id)} className={`p-4 rounded-xl border-2 text-left transition-all flex items-center justify-between ${selected.includes(opt.id) ? 'bg-indigo-600 border-indigo-500 shadow-lg' : 'bg-slate-800 border-slate-700 hover:border-slate-500'}`}>
                            <span className="font-bold text-white">{getPrimaryText(opt.label)}</span>
                            {selected.includes(opt.id) && <Check size={18} className="text-white"/>}
                        </button>
                    ))}
                </div>
             );
        case 'MATRIX':
            const matrixVal = (value as Record<string, string>) || {};
            const handleMatrixChange = (rId: string, cId: string) => {
                onChange({ ...matrixVal, [rId]: cId });
            };
            
            return (
                <div className="w-full">
                    {/* Desktop/Tablet Table View */}
                    <div className="hidden md:block overflow-x-auto rounded-xl border border-slate-700 bg-slate-800/50">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-slate-900/50">
                                    <th className="p-4 border-b border-slate-700 w-1/3"></th>
                                    {q.config.columns?.map(col => (
                                        <th key={col.id} className="p-4 text-center border-b border-slate-700 text-sm font-bold text-slate-300 min-w-[100px]">
                                            {getPrimaryText(col.label)}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-700">
                                {q.config.rows?.map((row, idx) => (
                                    <tr key={row.id} className={idx % 2 === 0 ? 'bg-slate-800/30' : 'transparent hover:bg-slate-700/20'}>
                                        <td className="p-4 font-medium text-white text-sm">
                                            {getPrimaryText(row.label)}
                                        </td>
                                        {q.config.columns?.map(col => (
                                            <td key={col.id} className="p-4 text-center">
                                                <label className="inline-flex items-center justify-center w-full h-full cursor-pointer group">
                                                    <input
                                                        type="radio"
                                                        name={`matrix-${q.id}-${row.id}`}
                                                        checked={matrixVal[row.id] === col.id}
                                                        onChange={() => handleMatrixChange(row.id, col.id)}
                                                        className="appearance-none w-6 h-6 rounded-full border-2 border-slate-500 checked:border-indigo-500 checked:bg-indigo-600 transition-all cursor-pointer relative"
                                                    />
                                                </label>
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile Card View */}
                    <div className="md:hidden space-y-4">
                        {q.config.rows?.map((row) => (
                            <div key={row.id} className="bg-slate-800 p-4 rounded-xl border border-slate-700">
                                <p className="font-bold text-white mb-3 text-sm">{getPrimaryText(row.label)}</p>
                                <div className="space-y-2">
                                    {q.config.columns?.map((col) => (
                                        <label key={col.id} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${matrixVal[row.id] === col.id ? 'bg-indigo-900/20 border-indigo-500' : 'bg-slate-900 border-slate-700'}`}>
                                            <input
                                                type="radio"
                                                name={`matrix-mobile-${q.id}-${row.id}`}
                                                checked={matrixVal[row.id] === col.id}
                                                onChange={() => handleMatrixChange(row.id, col.id)}
                                                className="accent-indigo-500"
                                            />
                                            <span className={`text-sm ${matrixVal[row.id] === col.id ? 'text-indigo-300 font-bold' : 'text-slate-400'}`}>
                                                {getPrimaryText(col.label)}
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            );
        case 'DATE':
            return <input type="date" value={value || ''} onChange={e => onChange(e.target.value)} className="w-full bg-slate-800 border border-slate-700 rounded-xl py-4 px-4 text-white text-xl focus:ring-2 focus:ring-indigo-500 outline-none [color-scheme:dark]" />;
        case 'GEO_LOCATION':
            return (
                <div className="flex flex-col items-center gap-6 py-8">
                     {value ? (
                         <div className="bg-emerald-500/10 border border-emerald-500/50 text-emerald-400 p-6 rounded-2xl flex flex-col items-center gap-3">
                             <MapPin size={32} />
                             <p className="font-mono">{value.lat.toFixed(5)}, {value.lng.toFixed(5)}</p>
                             <button onClick={() => onChange(null)} className="text-sm underline">Clear</button>
                         </div>
                     ) : (
                         <button onClick={() => {
                             // Guard: geolocation is absent on insecure origins/old browsers, and a
                             // denied permission must give feedback — not a silently dead button.
                             if (!navigator.geolocation) {
                                 alert('Location is not supported by this browser or connection.');
                                 return;
                             }
                             navigator.geolocation.getCurrentPosition(
                                 p => onChange({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }),
                                 () => alert('Could not get your location. Please allow location access and try again.'),
                                 { enableHighAccuracy: true, timeout: 10000 }
                             );
                         }} className="bg-indigo-600 text-white px-8 py-4 rounded-xl flex items-center gap-2 font-bold"><Locate size={24}/> Share Location</button>
                     )}
                </div>
            );
        case 'IMAGE_UPLOAD':
            const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
                if (e.target.files && e.target.files[0]) {
                    setUploading(true);
                    try {
                        const url = await uploadSurveyFile(e.target.files[0]);
                        onChange(url);
                    } catch (err) {
                        alert("Upload failed. Please try again.");
                    } finally {
                        setUploading(false);
                    }
                }
            };
            return (
                <div className="flex flex-col items-center gap-6 py-8">
                    {value ? (
                        <div className="relative group">
                            <img src={value} alt="Uploaded" className="max-h-64 rounded-xl border border-slate-700 shadow-lg" />
                            <button onClick={() => onChange(null)} className="absolute top-2 right-2 bg-red-600 text-white p-2 rounded-full shadow-lg hover:bg-red-500 transition-colors">
                                <Trash2 size={16} />
                            </button>
                        </div>
                    ) : (
                        <label className={`bg-slate-800 border-2 border-dashed border-slate-600 rounded-2xl p-12 flex flex-col items-center gap-4 transition-all group ${uploading ? 'opacity-60 cursor-wait' : 'cursor-pointer hover:bg-slate-700 hover:border-indigo-500'}`}>
                            <div className="w-16 h-16 bg-slate-700 group-hover:bg-indigo-500/20 rounded-full flex items-center justify-center text-slate-400 group-hover:text-indigo-400 transition-colors">
                                {uploading ? <Loader2 size={32} className="animate-spin" /> : <UploadCloud size={32} />}
                            </div>
                            <span className="text-slate-400 group-hover:text-white font-medium text-lg">{uploading ? 'Uploading…' : 'Click to Upload Image'}</span>
                            <input type="file" accept="image/*" onChange={handleUpload} disabled={uploading} className="hidden" />
                        </label>
                    )}
                </div>
            );
        case 'RANKING':
            const items = (value as string[]) || q.config.options?.map(o => o.id) || [];
            
            const moveItem = (index: number, direction: 'up' | 'down') => {
                const newItems = [...items];
                if (direction === 'up' && index > 0) {
                    [newItems[index], newItems[index - 1]] = [newItems[index - 1], newItems[index]];
                } else if (direction === 'down' && index < newItems.length - 1) {
                    [newItems[index], newItems[index + 1]] = [newItems[index + 1], newItems[index]];
                }
                onChange(newItems);
            };

            // Initialize if empty
            if (!value && q.config.options) {
                 // We need to set initial value to options order, but we can't do it in render.
                 // It will be handled by the user interacting or we accept default order.
                 // But better to force user to interact or just render the options in current order.
            }

            return (
                <div className="max-w-xl mx-auto space-y-3">
                    {items.map((itemId, index) => {
                        const option = q.config.options?.find(o => o.id === itemId);
                        if (!option) return null;
                        return (
                            <div key={itemId} className="bg-slate-800 p-4 rounded-xl border border-slate-700 flex items-center justify-between gap-4 animate-in slide-in-from-bottom-2">
                                <div className="flex items-center gap-4">
                                    <div className="w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center font-bold text-slate-400 text-sm">
                                        {index + 1}
                                    </div>
                                    <span className="font-medium text-white">{getPrimaryText(option.label)}</span>
                                </div>
                                <div className="flex flex-col gap-1">
                                    <button 
                                        onClick={() => moveItem(index, 'up')} 
                                        disabled={index === 0}
                                        className="p-1 hover:bg-slate-700 rounded text-slate-400 hover:text-white disabled:opacity-30"
                                    >
                                        <ArrowUp size={16} />
                                    </button>
                                    <button 
                                        onClick={() => moveItem(index, 'down')} 
                                        disabled={index === items.length - 1}
                                        className="p-1 hover:bg-slate-700 rounded text-slate-400 hover:text-white disabled:opacity-30"
                                    >
                                        <ArrowDown size={16} />
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                    {!value && (
                        <div className="text-center">
                            <button 
                                onClick={() => onChange(q.config.options?.map(o => o.id))} 
                                className="text-indigo-400 text-sm hover:underline"
                            >
                                Start Ranking
                            </button>
                        </div>
                    )}
                </div>
            );
        default:
            return <div className="text-center text-slate-500 p-8 border border-slate-700 rounded-xl">Unsupported Question Type</div>;
    }
};

export default SurveyResponseView;
